import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import { getAuth, getMultiFactorResolver, TotpMultiFactorGenerator } from 'firebase/auth';

import { OtpInput } from '@/common';
import { Label, LABEL_TYPE } from '@/components/generic';
import { getDefaultPrivateRoute } from '@/helpers';
import { showToast, ToastType } from '@/helpers/toast';
import { SentoaLogo } from '@/resources';
import { TestIds } from '@/shared/test-ids';

export const MfaVerifyForm = () => {
  const [t] = useTranslation('common');
  const navigate = useNavigate();
  const location = useLocation();
  const [totpCode, setTotpCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const mfaError = location.state?.mfaError;

  if (!mfaError) {
    return <Navigate to='/login' replace />;
  }

  const handleVerify = async () => {
    setIsLoading(true);
    try {
      const auth = getAuth();
      const resolver = getMultiFactorResolver(auth, mfaError);
      const assertion = TotpMultiFactorGenerator.assertionForSignIn(
        resolver.hints[0].uid,
        totpCode
      );

      await resolver.resolveSignIn(assertion);
      navigate(getDefaultPrivateRoute(), { replace: true });
    } catch {
      showToast(ToastType.ERROR, t('mfaInvalidCode'));
      setTotpCode('');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className='flex w-full flex-col gap-6' data-testid={TestIds.mfa.verifyForm}>
      <div className='flex w-full justify-center'>
        <SentoaLogo className='h-10 w-44' />
      </div>
      <div className='flex justify-center'>
        <div className='h-px w-full bg-blue-powder' />
      </div>
      <Label
        text={t('mfaVerifyTitle')}
        type={LABEL_TYPE.H1}
        className='w-full justify-center text-center text-xl'
      />
      <Label
        text={t('mfaVerifyDescription')}
        type={LABEL_TYPE.PARAGRAPH1}
        className='w-full justify-center px-2 text-center text-secondary-gray'
      />
      <div className='flex justify-center'>
        <OtpInput
          value={totpCode}
          onChange={setTotpCode}
          disabled={isLoading}
          testIdPrefix={TestIds.mfa.verifyOtpInput}
          onComplete={handleVerify}
        />
      </div>
    </div>
  );
};
